import { defineCollection } from "@rebasepro/cms-types";
import type { CollectionConfig } from "@rebasepro/types";
import { personName, playerOf } from "../people";
import playersCollection from "./players";

// A friend asking you to their game, from its lobby. One row per pair, the latest ask: it reaches you as a notification
// when you have them on, and as a card on your Friends page for half an hour either way. backend/src/invites.ts writes it.
const gameInvitesCollection = defineCollection({
    name: "Game invites",
    singularName: "Game invite",
    slug: "game_invites",
    table: "songbuzz_invite",
    description: "Friends asking friends to a game from its lobby, the latest ask of each pair.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operation: "delete", roles: ["admin"] },
    ],
    properties: {
        // "<from>><to>": one row a pair
        id: { name: "Id", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        fromWho: { name: "From (id)", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        toWho: { name: "To (id)", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        from: playerOf("From", "fromWho", "from", (): CollectionConfig => playersCollection),
        to: playerOf("To", "toWho", "to", (): CollectionConfig => playersCollection),
        room: { name: "Room", type: "string", validation: { required: true }, admin: { readOnly: true } },
        name: { name: "Their name in the game", type: "string", admin: { readOnly: true } },
        pushed: { name: "Notifications sent", type: "number", columnType: "integer", defaultValue: 0, admin: { readOnly: true } },
        at: { name: "Asked", type: "date", validation: { required: true }, admin: { readOnly: true } },
    },
    indexes: [
        { on: ["toWho", "at"], reason: "the asks waiting for someone, newest first" },
        { on: ["fromWho", "at"], reason: "how many asks someone sent in the last hour" },
    ],
    admin: {
        icon: "Send",
        group: "System",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: async ({ entity, context }) => `${entity.values.name || await personName(context, entity.values.fromWho)} → ${await personName(context, entity.values.toWho)}`,
            subtitle: ({ entity }) => {
                const n = Number(entity.values.pushed ?? 0);
                return [`Room ${entity.values.room}`, n ? `${n} ${n === 1 ? "notification" : "notifications"} sent` : "no notification sent"].join(" · ");
            },
            date: "at",
        },
        form: {
            sections: [
                { key: "invite", properties: ["from", "to", "name", "room", "pushed", "at"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["id", "fromWho", "toWho"] },
            ],
        },
    },
});

export default gameInvitesCollection;
